import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from '@radix-ui/react-popover';
import { Command } from 'cmdk';
import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { twMerge } from 'tailwind-merge';
import { useLoaderData } from '~/app/features/crew/claim-line-items/resolution/resolution';
import Badge from '~/components/Badge';
import Icon from '~/components/Icon';
import MediaDisplay from '~/components/MediaDisplay';
import { MultiSelect } from '~/components/form-controls/MultiSelect';
import { baseFieldStyles } from '~/components/form-controls/sharedFieldStyles';
import Virtualizer from '~/components/layout/virtualizer/Virtualizer';
import PaginationControls from '~/components/pagination/PaginationControls';
import usePagination from '~/components/pagination/usePagination';
import { useCurrencyFormatter } from '~/utils/formatters';
import ProductDisplay from './ProductDisplay';
import {
  Product,
  useExchangeFilters,
  useExchangeProducts,
  useExchangeSelection,
  Variant,
} from './exchangeOptions';

const mediaQuery = window.matchMedia('(min-width: 640px)'); // same breakpoint as `sm` prefix in TailwindCSS, with v4 this could use something along the lines of `getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-sm')` for the value
function useResponsiveProductsPerPage() {
  const matches = useSyncExternalStore(
    (onChange) => {
      mediaQuery.addEventListener('change', onChange);
      return () => mediaQuery.removeEventListener('change', onChange);
    },
    () => mediaQuery.matches,
  );

  return matches ? 8 : 4;
}

/** Relationship of an option name to values of the option. */
type Filters = Map<string, Set<string>>;

function ProductSelectionFilter({
  selected,
  onChange,
}: {
  selected: Filters;
  onChange: (selected: Filters) => void;
}) {
  const exchangeFilters = useExchangeFilters();

  if (!exchangeFilters.option.size) return null;

  return (
    <Popover>
      <PopoverTrigger
        // TODO: use IconButton here
        className="flex items-center gap-2 rounded-md border border-outline p-3 focus:ring-2 focus:ring-brand focus:ring-offset-2 focus:outline-none"
      >
        <Icon icon="Filter" className="size-4" />
        <span className="sr-only">Filter</span>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          align="end"
          sideOffset={4}
          className="min-w-3xs rounded-md border border-outline bg-surface-container-lowest shadow-md"
          data-component="corso-product-filter-popover"
        >
          <ul className="flex max-h-[50vh] flex-col gap-4 overflow-auto p-4">
            {[...exchangeFilters.option.entries()]
              .sort((a, b) => a[0].localeCompare(b[0]))
              .map(([name, values]) => (
                <li key={name}>
                  <MultiSelect
                    id={`option-${name}-filter`}
                    name={`option[${name}]`}
                    label={name}
                    value={[...(selected.get(name) ?? [])]}
                    onValueChange={(selectedValues) => {
                      const updated = new Map(selected);
                      if (!selectedValues.length) updated.delete(name);
                      else updated.set(name, new Set(selectedValues));
                      onChange(updated);
                    }}
                    options={[...values]
                      .sort((a, b) => a.localeCompare(b))
                      .map((value) => ({
                        label: value,
                        value,
                      }))}
                  />
                </li>
              ))}
          </ul>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
}

function CurrentFilters({
  selectedFilters,
  setSelectedFilters,
}: {
  selectedFilters: Filters;
  setSelectedFilters: (filters: Filters) => void;
}) {
  return (
    !!selectedFilters.size && (
      <ul className="flex flex-wrap items-center gap-2">
        <li>
          <button
            type="button"
            className="hover:brightness-97"
            onClick={() => {
              setSelectedFilters(new Map());
            }}
          >
            <Badge>Clear All</Badge>
          </button>
        </li>
        {[...selectedFilters.entries()].map(([name, values]) =>
          [...values].map((value) => (
            <li key={`${name}:${value}`}>
              <button
                type="button"
                className="hover:brightness-97"
                onClick={() => {
                  const updated = new Map(selectedFilters);
                  const existingValues = updated.get(name) ?? new Set();
                  existingValues.delete(value);
                  if (!existingValues.size) updated.delete(name);
                  else updated.set(name, existingValues);
                  setSelectedFilters(updated);
                }}
              >
                <Badge>
                  {/* only showing the `value`, although it's possible different options could share a value, this is unlikely, and it's less visually cluttered */}
                  {value}
                  <span className="sr-only">Remove Filter</span>
                  <Icon icon="XMark" className="size-3" />
                </Badge>
              </button>
            </li>
          )),
        )}
      </ul>
    )
  );
}

function filterCompare(search: string, value: string, keywords: string[]) {
  const escapeValue = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const compare = new RegExp(escapeValue, 'i');
  if (compare.test(value)) return 1;
  if (keywords.some((keyword) => compare.test(keyword))) return 1;
  return 0;
}

// although these are used as estimates, the virtualization works a lot better when these are more accurate
const PRODUCT_HEADER_SIZE = 40;
const VARIANT_SIZE = 64;

// TODO extract as shared for registrations
// * Switching to something like BaseUI combobox could replace this, product search on registrations and the combobox
function ProductSelectionSearch({
  products,
  onSelect,
  selectedFilters,
  setSelectedFilters,
}: {
  products: Product[];
  selectedFilters: Filters;
  setSelectedFilters: (filters: Filters) => void;
  onSelect: ({
    product,
    variant,
  }: {
    product: Product;
    variant: Variant;
  }) => void;
}) {
  const { draftClaimLineItem, advancedExchangeMethod } = useLoaderData();
  const searchTriggerRef = useRef<HTMLButtonElement>(null);
  const [inputValue, setInputValue] = useState('');
  const [triggerHeight, setTriggerHeight] = useState(0);
  const currencyFormatter = useCurrencyFormatter();

  useLayoutEffect(() => {
    if (!searchTriggerRef.current) return;
    setTriggerHeight(searchTriggerRef.current.offsetHeight);
  }, []);

  const items = useMemo(
    () =>
      products.flatMap((product) =>
        product.variants.map((variant) => ({
          product,
          variant,
          keywords: [
            variant.title,
            product.name,
            ...variant.options.map((o) => o.value),
            variant.sku,
          ].filter((k) => k !== undefined && k !== null),
        })),
      ),
    [products],
  );

  const groupedFilteredItems = useMemo(() => {
    const filtered = items.filter((item) =>
      filterCompare(inputValue, item.variant.idFromPlatform, item.keywords),
    );

    const grouped = new Map<Product, typeof items>();
    filtered.forEach((item) => {
      const group = grouped.get(item.product) ?? [];
      group.push(item);
      grouped.set(item.product, group);
    });

    return grouped;
  }, [inputValue, items]);

  const filteredProducts = useMemo(
    () => [...groupedFilteredItems.keys()],
    [groupedFilteredItems],
  );

  return (
    <Popover>
      <PopoverTrigger
        className={twMerge(
          baseFieldStyles,
          'flex w-full items-center gap-2 px-3 py-2',
        )}
        ref={searchTriggerRef}
      >
        <Icon icon="MagnifyingGlass" />
        <span>Search</span>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          className="max-h-(--radix-popover-content-available-height) w-(--radix-popover-trigger-width) max-w-(--radix-popover-content-available-width) origin-(--radix-popover-content-transform-origin)"
          sideOffset={-1 * triggerHeight}
        >
          <Command
            shouldFilter={false}
            className="rounded-md border border-outline bg-surface-container-lowest shadow-md"
            data-component="corso-product-search-popover"
          >
            <div className="space-y-2 border-b border-outline px-3 py-2">
              <div className="flex items-center gap-2">
                <Icon icon="MagnifyingGlass" className="size-4" />
                <Command.Input
                  className="w-full border-0 p-0 focus:ring-0"
                  value={inputValue}
                  onValueChange={setInputValue}
                />
              </div>
              <CurrentFilters
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
              />
            </div>

            <Command.Empty className="p-4 text-center">
              No results found.
            </Command.Empty>

            <Command.List className="overflow-auto rounded-b-md bg-outline *:[[cmdk-list-sizer]]:flex *:[[cmdk-list-sizer]]:flex-col *:[[cmdk-list-sizer]]:gap-px">
              <Virtualizer
                items={filteredProducts}
                estimateSize={(index) => {
                  const product = filteredProducts[index];
                  if (!product) return 0; // no estimate for missing product

                  const variants = groupedFilteredItems.get(product) ?? [];
                  return variants.length * VARIANT_SIZE + PRODUCT_HEADER_SIZE;
                }}
              >
                {(product) => {
                  // TODO: elevate condition into shared value for showing pricing // ALSO should we show on 'Payment' only... or only for items that are a higher price?
                  const showPricing =
                    advancedExchangeMethod === 'PAYMENT_AND_PARTIAL_REFUND';
                  return (
                    <Command.Group
                      key={product.idFromPlatform}
                      heading={
                        <header className="flex items-center justify-between gap-2 bg-surface-container-lowest px-4 py-2 label-lg">
                          {product.name}
                        </header>
                      }
                      className={twMerge(
                        'bg-outline-variant',
                        '*:[[cmdk-group-heading]]:sticky *:[[cmdk-group-heading]]:top-0 *:[[cmdk-group-heading]]:z-10',
                        '*:[[cmdk-group-items]]:flex *:[[cmdk-group-items]]:flex-col *:[[cmdk-group-items]]:gap-px',
                      )}
                    >
                      {groupedFilteredItems.get(product)?.map(({ variant }) => (
                        <Command.Item
                          className={twMerge(
                            'bg-surface-container-lowest px-4 py-2',
                            // hovered/highlighted
                            'aria-selected:bg-mix aria-selected:mix-with-on-surface/(--state-hovered-mix) aria-selected:mix-surface-container-lowest',
                          )}
                          key={variant.idFromPlatform}
                          value={`${variant.idFromPlatform}`}
                          onSelect={() => onSelect({ product, variant })}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <article className="flex items-center gap-2">
                              <div
                                data-component="corso-product-image"
                                className="rounded-lg"
                              >
                                <MediaDisplay
                                  type="image"
                                  src={variant.imgUrl ?? product.imageUrl}
                                  alt={variant.title}
                                  className="size-12 rounded-[inherit]"
                                />
                              </div>
                              <p className="flex items-center gap-2 body-md">
                                {product.hasOnlyDefaultVariant
                                  ? product.name
                                  : variant.options
                                      .map((option) => option.value)
                                      .join(' / ')}
                                {draftClaimLineItem.lineItem
                                  .variantIdFromPlatform ===
                                  variant.idFromPlatform && (
                                  <Badge>Current Product</Badge>
                                )}
                              </p>
                            </article>
                            <span className="label-lg" hidden={!showPricing}>
                              {currencyFormatter(variant.price)}
                            </span>
                          </div>
                        </Command.Item>
                      ))}
                    </Command.Group>
                  );
                }}
              </Virtualizer>
            </Command.List>
          </Command>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
}

export default function ProductSelectionGrid() {
  const [selectedFilters, setSelectedFilters] = useState<Filters>(new Map());
  const [{ selectedProduct }, dispatch] = useExchangeSelection();
  const {
    draftClaimLineItem,
    advancedExchangeMethod,
    isFilterAndSearchEnabled,
  } = useLoaderData();
  const exchangeProducts = useExchangeProducts();

  // ? might need to normalize option names/values in the future to account for casing differences at least
  const filteredProducts = useMemo(() => {
    if (!selectedFilters.size) return exchangeProducts;

    return exchangeProducts
      .map((product) => ({
        ...product,
        variants: product.variants.filter((variant) =>
          [...selectedFilters.entries()].every(([name, values]) =>
            variant.options.some(
              (option) => option.name === name && values.has(option.value),
            ),
          ),
        ),
      }))
      .filter((product) => !!product.variants.length);
  }, [exchangeProducts, selectedFilters]);

  const productsPerPage = useResponsiveProductsPerPage();
  const [products, pagination] = usePagination({
    items: filteredProducts,
    itemsPerPage: productsPerPage,
  });

  if (selectedProduct) return null;

  if (!exchangeProducts.length) return null;

  return (
    <>
      <div className="mb-4 flex flex-col gap-4">
        {isFilterAndSearchEnabled && (
          <div className="flex items-center justify-between gap-2">
            <div className={twMerge('grow')}>
              <ProductSelectionSearch
                products={filteredProducts}
                onSelect={({ product, variant }) => {
                  dispatch({ type: 'setVariant', product, variant });
                }}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
              />
            </div>

            <div>
              <ProductSelectionFilter
                selected={selectedFilters}
                onChange={setSelectedFilters}
              />
            </div>
          </div>
        )}
        <CurrentFilters
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
        />
        {!filteredProducts.length && (
          <p className="body-md text-on-surface-variant">No products found.</p>
        )}

        <div
          className="grid grid-cols-2 gap-4 sm:grid-cols-4"
          data-testid="product-grid"
        >
          {/* // TODO consider an aria grid so focus can quickly skip these */}
          {products.map((product) => (
            <ProductDisplay
              advancedExchangeMethod={advancedExchangeMethod}
              key={product.idFromPlatform}
              product={product}
              banner={
                product.idFromPlatform ===
                draftClaimLineItem.lineItem.productIdFromPlatform
                  ? 'Current Product'
                  : null
              }
              onSelect={() => dispatch({ type: 'setProduct', product })}
            />
          ))}
        </div>
      </div>

      <PaginationControls
        // oxlint-disable-next-line react/jsx-props-no-spreading
        {...pagination}
        className="mx-auto"
      />
    </>
  );
}
